(() => {
  const initialized = new WeakSet();

  function syncModelGuides(variantId) {
    document.querySelectorAll('[data-ow-model-card]').forEach((card) => {
      card.classList.toggle('is-selected', card.dataset.owModelCard === String(variantId));
    });
    document.querySelectorAll('[data-ow-select-variant]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.owSelectVariant === String(variantId)));
    });
  }

  function initProductSection(section) {
    if (!section || initialized.has(section)) return;
    initialized.add(section);
    const controller = new AbortController();
    const listenerOptions = { signal: controller.signal };
    let overlayObserver = null;
    let stickyResizeObserver = null;
    let lastStickyHeight = 0;

    const form = section.querySelector('[data-ow-product-form]');
    const hiddenId = section.querySelector('[data-ow-variant-id]');
    const price = section.querySelector('[data-ow-price]');
    const stock = section.querySelector('[data-ow-stock]');
    const addButton = section.querySelector('[data-ow-add]');
    const paymentButton = section.querySelector('[data-ow-payment-button]');
    const quantity = section.querySelector('[data-ow-quantity]');
    const image = section.querySelector('.ow-product__image');
    const productUrl = section.dataset.owProductUrl;
    const stickyBuy = section.querySelector('[data-ow-sticky-buy]');
    const topPurchase = section.querySelector('[data-ow-top-purchase]');
    const nativePayment = section.querySelector('[data-ow-native-payment]');
    const stickyModel = section.querySelector('[data-ow-sticky-model]');
    const stickyPrice = section.querySelector('[data-ow-sticky-price]');
    const stickyStock = section.querySelector('[data-ow-sticky-stock]');
    const stickyQuantity = section.querySelector('[data-ow-sticky-quantity]');
    let stickyFrame = 0;

    function blockingOverlayIsOpen() {
      const selectors = [
        'dialog[open]',
        'menu-drawer details[open]',
        'details.menu-drawer-container[open]',
        'details-modal details[open]',
        'cart-drawer.active',
        '#shopify-pc__banner:not([hidden])',
        '.shopify-pc__banner__dialog:not([hidden])',
        '[data-shopify-privacy-banner]:not([hidden])',
        'iframe[title*="privacy" i]',
        'iframe[title*="cookie" i]',
      ];
      return selectors.some((selector) => {
        const element = document.querySelector(selector);
        if (!element) return false;
        const style = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0 && rect.width > 0 && rect.height > 0;
      });
    }

    function updateStickyVisibility() {
      if (!stickyBuy || !topPurchase || !nativePayment) return;
      const selected = section.querySelector('[data-ow-variant-radio]:checked');
      const available = selected ? selected.dataset.available === 'true' : !addButton?.disabled;
      const topPurchaseHasScrolledAbove = topPurchase.getBoundingClientRect().bottom <= 0;
      const show = available && topPurchaseHasScrolledAbove && !blockingOverlayIsOpen();

      if (show) {
        stickyBuy.classList.add('is-fixed');
        stickyBuy.setAttribute('aria-label', '固定購入バー');
        document.body.classList.add('ow-sticky-buy-visible');
        const stickyHeight = Math.ceil(stickyBuy.getBoundingClientRect().height);
        if (stickyHeight !== lastStickyHeight) {
          lastStickyHeight = stickyHeight;
          document.documentElement.style.setProperty('--ow-sticky-buy-height', `${stickyHeight}px`);
        }
      } else {
        stickyBuy.classList.remove('is-fixed');
        stickyBuy.setAttribute('aria-label', '購入操作');
        document.body.classList.remove('ow-sticky-buy-visible');
        document.documentElement.style.removeProperty('--ow-sticky-buy-height');
        lastStickyHeight = 0;
      }
    }

    function scheduleStickyUpdate() {
      if (stickyFrame) return;
      stickyFrame = window.requestAnimationFrame(() => {
        stickyFrame = 0;
        updateStickyVisibility();
      });
    }

    function normalizeQuantity(rawValue, minimum, maximum, increment) {
      const safeMinimum = Number.isFinite(minimum) ? minimum : 1;
      const safeIncrement = Number.isFinite(increment) && increment > 0 ? increment : 1;
      let next = Number.isFinite(rawValue) ? rawValue : safeMinimum;
      next = Math.max(safeMinimum, next);
      next = safeMinimum + Math.floor((next - safeMinimum) / safeIncrement) * safeIncrement;
      if (Number.isFinite(maximum)) {
        const maxOnStep = safeMinimum + Math.max(0, Math.floor((maximum - safeMinimum) / safeIncrement)) * safeIncrement;
        next = Math.min(next, maxOnStep);
      }
      return next;
    }

    function syncVariant(radio, updateUrl) {
      if (!radio || !radio.checked) return;
      const available = radio.dataset.available === 'true';
      hiddenId.value = radio.value;
      hiddenId.disabled = !available;
      hiddenId.dispatchEvent(new Event('change', { bubbles: true }));
      if (price) price.textContent = radio.dataset.price;
      stock.textContent = available ? '在庫あり' : '売り切れ';
      addButton.disabled = !available;
      addButton.textContent = available ? 'カートに追加' : '売り切れ';
      if (paymentButton) paymentButton.hidden = !available;
      if (quantity) {
        const minimum = Number.parseInt(radio.dataset.quantityMin || '1', 10);
        const increment = Number.parseInt(radio.dataset.quantityIncrement || '1', 10);
        const maximum = Number.parseInt(radio.dataset.quantityMax || '', 10);
        quantity.min = String(minimum);
        quantity.step = String(increment);
        if (Number.isFinite(maximum)) quantity.max = String(maximum);
        else quantity.removeAttribute('max');
        quantity.value = String(normalizeQuantity(Number.parseInt(quantity.value || '', 10), minimum, maximum, increment));
      }
      if (image && radio.dataset.imageSrc) {
        image.src = radio.dataset.imageSrc;
        image.removeAttribute('srcset');
        image.alt = radio.dataset.imageAlt || image.alt;
      }
      if (stickyModel) stickyModel.textContent = radio.dataset.title || radio.value;
      if (stickyPrice) stickyPrice.textContent = radio.dataset.price;
      if (stickyStock) stickyStock.textContent = available ? '在庫あり' : '売り切れ';
      if (stickyQuantity && quantity) stickyQuantity.textContent = quantity.value;
      syncModelGuides(radio.value);
      if (updateUrl && productUrl && window.history?.replaceState) {
        const nextUrl = new URL(productUrl, window.location.origin);
        nextUrl.searchParams.set('variant', radio.value);
        window.history.replaceState({}, '', nextUrl);
      }
      scheduleStickyUpdate();
    }

    section.querySelectorAll('[data-ow-variant-radio]').forEach((radio) => {
      radio.addEventListener('change', () => syncVariant(radio, true));
    });
    syncVariant(section.querySelector('[data-ow-variant-radio]:checked'), false);

    if (form) {
      form.addEventListener('submit', () => {
        addButton.setAttribute('aria-busy', 'true');
      });
    }

    if (quantity) {
      quantity.addEventListener('input', () => {
        if (stickyQuantity) stickyQuantity.textContent = quantity.value;
      }, listenerOptions);
      const changeQuantity = (direction) => {
        const minimum = Number.parseInt(quantity.min || '1', 10);
        const maximum = Number.parseInt(quantity.max || '', 10);
        const increment = Number.parseInt(quantity.step || '1', 10);
        const current = normalizeQuantity(Number.parseInt(quantity.value || '', 10), minimum, maximum, increment);
        const next = normalizeQuantity(current + direction * increment, minimum, maximum, increment);
        quantity.value = String(next);
        quantity.dispatchEvent(new Event('input', { bubbles: true }));
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      };
      section.querySelector('[data-ow-quantity-decrease]')?.addEventListener('click', () => changeQuantity(-1));
      section.querySelector('[data-ow-quantity-increase]')?.addEventListener('click', () => changeQuantity(1));
    }

    const gallery = section.querySelector('[data-ow-gallery]');
    if (gallery) {
      const slides = Array.from(gallery.querySelectorAll('[data-ow-gallery-slide]'));
      const thumbnails = Array.from(gallery.querySelectorAll('[data-ow-gallery-thumbnail]'));
      let activeIndex = Math.max(0, slides.findIndex((slide) => !slide.hidden));
      const showSlide = (requestedIndex) => {
        if (!slides.length) return;
        activeIndex = (requestedIndex + slides.length) % slides.length;
        slides.forEach((slide, index) => {
          const active = index === activeIndex;
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
        });
        thumbnails.forEach((thumbnail, index) => {
          const active = index === activeIndex;
          thumbnail.classList.toggle('is-active', active);
          thumbnail.setAttribute('aria-current', String(active));
        });
      };
      thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener('click', () => showSlide(index)));
      gallery.querySelector('[data-ow-gallery-previous]')?.addEventListener('click', () => showSlide(activeIndex - 1));
      gallery.querySelector('[data-ow-gallery-next]')?.addEventListener('click', () => showSlide(activeIndex + 1));
      showSlide(activeIndex);
    }

    const dialog = section.querySelector('[data-ow-dialog]');
    const dialogImage = section.querySelector('[data-ow-dialog-image]');
    const closeButton = section.querySelector('[data-ow-dialog-close]');
    if (dialog && dialogImage && closeButton && typeof dialog.showModal === 'function') {
      let activeTrigger = null;
      section.querySelectorAll('[data-ow-zoom]').forEach((trigger) => trigger.addEventListener('click', () => {
        const currentImage = trigger.querySelector('img');
        if (!currentImage) return;
        activeTrigger = trigger;
        dialogImage.src = currentImage.currentSrc || currentImage.src;
        dialogImage.alt = currentImage.alt;
        dialog.showModal();
        closeButton.focus();
        scheduleStickyUpdate();
      }));
      closeButton.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => {
        dialogImage.removeAttribute('src');
        activeTrigger?.focus();
        scheduleStickyUpdate();
      });
    }

    if (stickyBuy && topPurchase && nativePayment) {
      window.addEventListener('scroll', scheduleStickyUpdate, { passive: true, signal: controller.signal });
      window.addEventListener('resize', scheduleStickyUpdate, { passive: true, signal: controller.signal });
      document.addEventListener('click', scheduleStickyUpdate, { capture: true, signal: controller.signal });
      document.addEventListener('keyup', scheduleStickyUpdate, listenerOptions);
      document.addEventListener('toggle', scheduleStickyUpdate, { capture: true, signal: controller.signal });
      if ('MutationObserver' in window) {
        overlayObserver = new MutationObserver(scheduleStickyUpdate);
        overlayObserver.observe(document.body, {
          subtree: true,
          childList: true,
        });
      }
      if ('ResizeObserver' in window) {
        stickyResizeObserver = new ResizeObserver(() => {
          if (stickyBuy.classList.contains('is-fixed')) scheduleStickyUpdate();
        });
        stickyResizeObserver.observe(stickyBuy);
      }
      updateStickyVisibility();
    }

    section.owCleanup = () => {
      controller.abort();
      overlayObserver?.disconnect();
      stickyResizeObserver?.disconnect();
      if (stickyFrame) window.cancelAnimationFrame(stickyFrame);
      if (stickyBuy?.classList.contains('is-fixed')) {
        document.body.classList.remove('ow-sticky-buy-visible');
        document.documentElement.style.removeProperty('--ow-sticky-buy-height');
        lastStickyHeight = 0;
      }
    };
  }

  function initModelGuide(guide) {
    if (!guide || initialized.has(guide)) return;
    initialized.add(guide);
    guide.querySelectorAll('[data-ow-select-variant]').forEach((button) => {
      button.addEventListener('click', () => {
        const radio = Array.from(document.querySelectorAll('[data-ow-variant-radio]')).find(
          (candidate) => candidate.value === button.dataset.owSelectVariant,
        );
        if (!radio || radio.disabled) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        radio.focus({ preventScroll: true });
        document.querySelector('[data-ow-product-form]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  function init(root = document) {
    if (root.matches?.('[data-ow-product-section]')) initProductSection(root);
    if (root.matches?.('[data-ow-model-guide]')) initModelGuide(root);
    root.querySelectorAll('[data-ow-product-section]').forEach(initProductSection);
    root.querySelectorAll('[data-ow-model-guide]').forEach(initModelGuide);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const productSection = event.target.matches?.('[data-ow-product-section]')
      ? event.target
      : event.target.querySelector?.('[data-ow-product-section]');
    productSection?.owCleanup?.();
  });
})();
